"use client";

import { CheckCircle2Icon, CircleAlertIcon, InfoIcon, TriangleAlertIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../lib";

export const STATUS_CALLOUT_VARIANTS = [
	"info",
	"success",
	"warning",
	"error",
	"neutral",
] as const;

export type StatusCalloutVariant = (typeof STATUS_CALLOUT_VARIANTS)[number];

export type StatusCalloutSize = "default" | "compact";

export interface StatusCalloutProps {
	variant: StatusCalloutVariant;
	size?: StatusCalloutSize;
	title?: ReactNode;
	children?: ReactNode;
	icon?: ReactNode;
	className?: string;
	/** Override default role for the variant. */
	role?: "alert" | "note" | "status";
	id?: string;
}

const VARIANT_CHROME: Record<
	StatusCalloutVariant,
	{ shell: string; title: string; body: string; icon: string }
> = {
	error: {
		shell: "border-destructive/40 bg-destructive/5",
		title: "text-destructive",
		body: "text-destructive",
		icon: "text-destructive",
	},
	warning: {
		shell: "border-amber-500/40 bg-amber-500/5",
		title: "text-amber-950 dark:text-amber-100",
		body: "text-amber-950 dark:text-amber-100",
		icon: "text-amber-600 dark:text-amber-400",
	},
	info: {
		shell: "border-primary/30 bg-primary/5",
		title: "text-foreground",
		body: "text-muted-foreground",
		icon: "text-primary",
	},
	success: {
		shell: "border-emerald-500/40 bg-emerald-500/5",
		title: "text-emerald-950 dark:text-emerald-100",
		body: "text-emerald-950 dark:text-emerald-100",
		icon: "text-emerald-600 dark:text-emerald-400",
	},
	neutral: {
		shell: "border-border bg-muted/40",
		title: "text-foreground",
		body: "text-muted-foreground",
		icon: "text-muted-foreground",
	},
};

function defaultRole(variant: StatusCalloutVariant): "alert" | "note" | "status" {
	if (variant === "error") {
		return "alert";
	}
	if (variant === "success") {
		return "status";
	}
	return "note";
}

function DefaultIcon({
	variant,
	className,
}: {
	variant: StatusCalloutVariant;
	className?: string;
}) {
	const iconClass = cn("size-4 shrink-0", className);
	switch (variant) {
		case "error":
			return <CircleAlertIcon className={iconClass} aria-hidden />;
		case "warning":
			return <TriangleAlertIcon className={iconClass} aria-hidden />;
		case "success":
			return <CheckCircle2Icon className={iconClass} aria-hidden />;
		case "info":
		case "neutral":
			return <InfoIcon className={iconClass} aria-hidden />;
		default: {
			const _exhaustive: never = variant;
			return _exhaustive;
		}
	}
}

/**
 * Shared status / field callout chrome (info, success, warning, error, neutral).
 * Sheet SoT: ScopeWarning / ElevenLabs v3 amber callouts; field errors use `size="compact"`.
 */
export function StatusCallout({
	variant,
	size = "default",
	title,
	children,
	icon,
	className,
	role,
	id,
}: StatusCalloutProps) {
	const chrome = VARIANT_CHROME[variant];
	const isCompact = size === "compact";

	return (
		<div
			id={id}
			role={role ?? defaultRole(variant)}
			data-status-callout={variant}
			data-size={size}
			className={cn(
				"rounded-md border",
				chrome.shell,
				isCompact ? "px-2 py-1.5" : "px-3 py-2.5",
				className,
			)}
		>
			<div className="flex items-center gap-2">
				{icon === null
					? null
					: (icon ?? (
							<span className={chrome.icon}>
								<DefaultIcon variant={variant} />
							</span>
						))}
				<div className="min-w-0 flex-1 space-y-0.5">
					{title ? (
						<p
							className={cn(
								"font-semibold",
								isCompact ? "text-[11px]" : "text-xs",
								chrome.title,
							)}
						>
							{title}
						</p>
					) : null}
					{children ? (
						<div
							className={cn(
								"leading-snug",
								isCompact ? "text-[11px]" : "text-[11px]",
								chrome.body,
							)}
						>
							{children}
						</div>
					) : null}
				</div>
			</div>
		</div>
	);
}
